import React from 'react';
import { Globe, Monitor, Smartphone, Check } from 'lucide-react';
import { BuildPlatform, BUILD_TARGETS } from '../../../../core/types/build.types';
import { cn } from '../../../utils/cn';

interface Props {
  selected: BuildPlatform;
  onSelect: (platform: BuildPlatform) => void;
}

const PlatformIcon: React.FC<{ platform: BuildPlatform; size?: number }> = ({ platform, size = 24 }) => {
  switch (platform) {
    case 'web':
      return <Globe size={size} />;
    case 'desktop':
      return <Monitor size={size} />;
    case 'mobile':
      return <Smartphone size={size} />;
    default:
      return <Globe size={size} />;
  }
};

export const PlatformSelector: React.FC<Props> = ({ selected, onSelect }) => {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-zinc-300">Target Platform</h3>

      <div className="grid grid-cols-3 gap-3">
        {BUILD_TARGETS.map((target) => (
          <button
            key={target.platform}
            onClick={() => target.enabled && onSelect(target.platform)}
            disabled={!target.enabled}
            className={cn(
              'relative flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
              selected === target.platform
                ? 'border-blue-500 bg-blue-500/10'
                : target.enabled
                  ? 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                  : 'border-zinc-800 bg-zinc-900/50 opacity-50 cursor-not-allowed'
            )}
          >
            {/* Selection indicator */}
            {selected === target.platform && (
              <div className="absolute top-2 right-2">
                <Check size={16} className="text-blue-400" />
              </div>
            )}

            {/* Icon */}
            <div className={cn(
              'p-3 rounded-full',
              selected === target.platform ? 'bg-blue-500/20 text-blue-400' : 'bg-zinc-700 text-zinc-400'
            )}>
              <PlatformIcon platform={target.platform} size={24} />
            </div>

            {/* Name */}
            <span className={cn(
              'text-sm font-medium',
              selected === target.platform ? 'text-blue-300' : 'text-zinc-300'
            )}>
              {target.name}
            </span>

            {/* Description */}
            <span className="text-xs text-zinc-500 text-center">
              {target.description}
            </span>

            {/* Coming soon badge */}
            {!target.enabled && (
              <span className="absolute top-2 left-2 px-1.5 py-0.5 text-xs bg-zinc-700 text-zinc-400 rounded">
                Soon
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
